import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Loader2,
  Search,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle2,
  DollarSign,
  Plus,
  X,
  CreditCard,
  ShieldCheck,
  Ban,
  Trash2,
} from "lucide-react";
import {
  billsApi,
  chartOfAccountsApi,
  bankAccountsApi,
  taxSettingsApi,
} from "../../services/api";
import { TaxConfig, calculateTaxCents, getTaxLabel, getEffectiveRate, DEFAULT_TAX_CONFIG } from "../../utils/taxCalculation";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BillLineItem {
  description: string;
  accountId: string | { _id: string; code: string; name: string };
  amountCents: number;
  taxable: boolean;
  taxAmountCents: number;
  matterId: string | null;
  clientId: string | null;
}

interface Bill {
  _id: string;
  vendorName: string;
  vendorContactId: string | null;
  billNumber: string | null;
  billDate: string;
  dueDate: string;
  lineItems: BillLineItem[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
  status: "draft" | "approved" | "paid" | "partial" | "void";
  paymentMethod: string | null;
  paidAt: string | null;
  bankAccountId: string | null;
  notes: string | null;
  createdAt: string;
}

interface AgingData {
  current: { count: number; totalCents: number };
  "1-30": { count: number; totalCents: number };
  "31-60": { count: number; totalCents: number };
  "60+": { count: number; totalCents: number };
}

interface LineItemForm {
  description: string;
  accountId: string;
  amountDollars: string;
  taxable: boolean;
  taxAmountDollars: string;
}

interface ExpenseAccount {
  _id: string;
  code: string;
  name: string;
  type: string;
}

interface BankAccount {
  _id: string;
  name: string;
  accountNumber?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return cents < 0 ? `-${formatted}` : formatted;
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function daysOverdue(dueDate: string | null): number {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  const now = new Date();
  const diff = Math.floor(
    (now.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)
  );
  return Math.max(0, diff);
}

type AgingBucket = "current" | "1-30" | "31-60" | "60+";

function getAgingBucket(dueDate: string | null): AgingBucket {
  const days = daysOverdue(dueDate);
  if (days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  return "60+";
}

const agingLabels: Record<AgingBucket, string> = {
  current: "Current",
  "1-30": "1-30 Days",
  "31-60": "31-60 Days",
  "60+": "60+ Days",
};

const agingColors: Record<
  AgingBucket,
  { bg: string; text: string; border: string }
> = {
  current: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  "1-30": {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  "31-60": {
    bg: "bg-orange-50",
    text: "text-orange-700",
    border: "border-orange-200",
  },
  "60+": { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
};

const statusBadgeClasses: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  approved: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  partial: "bg-purple-100 text-purple-700",
  void: "bg-red-100 text-red-700",
};

const statusLabels: Record<string, string> = {
  draft: "Draft",
  approved: "Approved",
  paid: "Paid",
  partial: "Partial",
  void: "Void",
};

const PAYMENT_METHODS = [
  { value: "eft", label: "EFT" },
  { value: "cheque", label: "Cheque" },
  { value: "wire", label: "Wire" },
  { value: "credit_card", label: "Credit Card" },
  { value: "cash", label: "Cash" },
];

function dollarsToCents(val: string): number {
  const num = parseFloat(val);
  if (isNaN(num)) return 0;
  return Math.round(num * 100);
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

const emptyLine = (): LineItemForm => ({
  description: "",
  accountId: "",
  amountDollars: "",
  taxable: false,
  taxAmountDollars: "0.00",
});

// ─── Component ────────────────────────────────────────────────────────────────

const AccountsPayableTab: React.FC = () => {
  // Core state
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Aging state
  const [aging, setAging] = useState<AgingData>({
    current: { count: 0, totalCents: 0 },
    "1-30": { count: 0, totalCents: 0 },
    "31-60": { count: 0, totalCents: 0 },
    "60+": { count: 0, totalCents: 0 },
  });

  // Create/edit modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [saving, setSaving] = useState(false);

  // Bill form state
  const [formVendorName, setFormVendorName] = useState("");
  const [formBillNumber, setFormBillNumber] = useState("");
  const [formBillDate, setFormBillDate] = useState(todayISO());
  const [formDueDate, setFormDueDate] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formLineItems, setFormLineItems] = useState<LineItemForm[]>([
    emptyLine(),
  ]);

  // Pay modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingBill, setPayingBill] = useState<Bill | null>(null);
  const [payAmountDollars, setPayAmountDollars] = useState("");
  const [payMethod, setPayMethod] = useState<string | null>(null);
  const [payBankAccountId, setPayBankAccountId] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  // Reference data
  const [expenseAccounts, setExpenseAccounts] = useState<ExpenseAccount[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // Hover states for styled buttons
  const [newBillHover, setNewBillHover] = useState(false);
  const [saveDraftHover, setSaveDraftHover] = useState(false);
  const [saveApproveHover, setSaveApproveHover] = useState(false);
  const [confirmPayHover, setConfirmPayHover] = useState(false);

  // Tax config
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  useEffect(() => {
    taxSettingsApi.get().then((res) => {
      const d = res.data?.data || res.data;
      if (d?.taxMode) {
        setTaxConfig({ taxMode: d.taxMode, hstRate: d.hstRate ?? 13, gstRate: d.gstRate ?? 5, pstRate: d.pstRate ?? 0 });
      }
    }).catch(() => {});
  }, []);

  // ─── Data Loading ───────────────────────────────────────────────────────────

  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billsApi.list({ limit: 500 });
      const data = res.data?.data || res.data || [];
      setBills(data);
    } catch (err) {
      console.error("Failed to load bills:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAging = useCallback(async () => {
    try {
      const res = await billsApi.aging();
      if (res.data) {
        setAging(res.data);
      }
    } catch {
      // Will compute locally from bills as fallback
    }
  }, []);

  const loadExpenseAccounts = useCallback(async () => {
    try {
      const res = await chartOfAccountsApi.list();
      setExpenseAccounts(res.data?.data?.expense || []);
    } catch (err) {
      console.error("Failed to load expense accounts:", err);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    try {
      const res = await bankAccountsApi.list({ limit: 100 });
      const data = res.data?.data || res.data || [];
      setBankAccounts(data);
    } catch (err) {
      console.error("Failed to load bank accounts:", err);
    }
  }, []);

  useEffect(() => {
    loadBills();
    loadAging();
    loadExpenseAccounts();
    loadBankAccounts();
  }, [loadBills, loadAging, loadExpenseAccounts, loadBankAccounts]);

  // ─── Computed Aging (fallback if API aging fails) ───────────────────────────

  const computedAging: AgingData = (() => {
    // If the API returned non-zero data, use it
    const apiTotal =
      aging.current.totalCents +
      aging["1-30"].totalCents +
      aging["31-60"].totalCents +
      aging["60+"].totalCents;

    if (apiTotal > 0) return aging;

    // Otherwise compute from loaded bills (exclude paid and void)
    const result: AgingData = {
      current: { count: 0, totalCents: 0 },
      "1-30": { count: 0, totalCents: 0 },
      "31-60": { count: 0, totalCents: 0 },
      "60+": { count: 0, totalCents: 0 },
    };

    bills
      .filter((b) => b.status !== "paid" && b.status !== "void")
      .forEach((b) => {
        const bucket = getAgingBucket(b.dueDate);
        const balance =
          b.balanceDueCents ?? b.totalCents - (b.paidCents || 0);
        result[bucket].count += 1;
        result[bucket].totalCents += balance;
      });

    return result;
  })();

  const totalOutstanding =
    computedAging.current.totalCents +
    computedAging["1-30"].totalCents +
    computedAging["31-60"].totalCents +
    computedAging["60+"].totalCents;

  const totalOutstandingCount =
    computedAging.current.count +
    computedAging["1-30"].count +
    computedAging["31-60"].count +
    computedAging["60+"].count;

  // ─── Filtering & Sorting ────────────────────────────────────────────────────

  const filtered = search
    ? bills.filter((b) => {
        const q = search.toLowerCase();
        return (
          (b.vendorName || "").toLowerCase().includes(q) ||
          (b.billNumber || "").toLowerCase().includes(q)
        );
      })
    : bills;

  const sorted = [...filtered].sort((a, b) => {
    // Show overdue first, then by due date
    const aOverdue = daysOverdue(a.dueDate);
    const bOverdue = daysOverdue(b.dueDate);
    return bOverdue - aOverdue;
  });

  // ─── Form Helpers ───────────────────────────────────────────────────────────

  const resetForm = () => {
    setFormVendorName("");
    setFormBillNumber("");
    setFormBillDate(todayISO());
    setFormDueDate("");
    setFormNotes("");
    setFormLineItems([emptyLine()]);
  };

  const openCreateModal = () => {
    resetForm();
    setEditingBill(null);
    setShowCreateModal(true);
  };

  const openEditModal = (bill: Bill) => {
    setEditingBill(bill);
    setFormVendorName(bill.vendorName || "");
    setFormBillNumber(bill.billNumber || "");
    setFormBillDate(bill.billDate ? bill.billDate.split("T")[0] : todayISO());
    setFormDueDate(bill.dueDate ? bill.dueDate.split("T")[0] : "");
    setFormNotes(bill.notes || "");
    setFormLineItems(
      bill.lineItems.length > 0
        ? bill.lineItems.map((li) => ({
            description: li.description || "",
            accountId:
              typeof li.accountId === "object" && li.accountId
                ? li.accountId._id
                : (li.accountId as string) || "",
            amountDollars: centsToDollars(li.amountCents),
            taxable: li.taxable,
            taxAmountDollars: centsToDollars(li.taxAmountCents),
          }))
        : [emptyLine()]
    );
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setEditingBill(null);
    resetForm();
  };

  const updateLineItem = (
    index: number,
    field: keyof LineItemForm,
    value: string | boolean
  ) => {
    setFormLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-calculate tax if taxable toggled or amount changed
      if (field === "taxable" || field === "amountDollars") {
        const item = updated[index];
        if (item.taxable) {
          const amountCents = dollarsToCents(item.amountDollars);
          const { totalTaxCents } = calculateTaxCents(amountCents, taxConfig);
          item.taxAmountDollars = (totalTaxCents / 100).toFixed(2);
        } else {
          item.taxAmountDollars = "0.00";
        }
      }

      return updated;
    });
  };

  const addLineItem = () => {
    setFormLineItems((prev) => [...prev, emptyLine()]);
  };

  const removeLineItem = (index: number) => {
    setFormLineItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const formSubtotalCents = formLineItems.reduce(
    (sum, li) => sum + dollarsToCents(li.amountDollars),
    0
  );

  const formTaxCents = formLineItems.reduce(
    (sum, li) => sum + dollarsToCents(li.taxAmountDollars),
    0
  );

  const formTotalCents = formSubtotalCents + formTaxCents;

  // ─── Save Bill ──────────────────────────────────────────────────────────────

  const handleSave = async (andApprove: boolean) => {
    if (!formVendorName.trim() || !formBillDate || !formDueDate) return;

    setSaving(true);
    try {
      const payload = {
        vendorName: formVendorName.trim(),
        billNumber: formBillNumber.trim() || null,
        billDate: formBillDate,
        dueDate: formDueDate,
        notes: formNotes.trim() || null,
        lineItems: formLineItems
          .filter((li) => li.description.trim() || dollarsToCents(li.amountDollars) > 0)
          .map((li) => ({
            description: li.description.trim(),
            accountId: li.accountId || null,
            amountCents: dollarsToCents(li.amountDollars),
            taxable: li.taxable,
            taxAmountCents: dollarsToCents(li.taxAmountDollars),
            matterId: null,
            clientId: null,
          })),
      };

      let saved: Bill;
      if (editingBill) {
        const res = await billsApi.update(editingBill._id, payload);
        saved = res.data;
      } else {
        const res = await billsApi.create(payload);
        saved = res.data;
      }

      if (andApprove && saved._id && saved.status === "draft") {
        await billsApi.approve(saved._id);
      }

      closeCreateModal();
      loadBills();
      loadAging();
    } catch (err) {
      console.error("Failed to save bill:", err);
    } finally {
      setSaving(false);
    }
  };

  // ─── Approve Bill ───────────────────────────────────────────────────────────

  const handleApprove = async (bill: Bill) => {
    try {
      await billsApi.approve(bill._id);
      loadBills();
      loadAging();
    } catch (err) {
      console.error("Failed to approve bill:", err);
    }
  };

  // ─── Void Bill ──────────────────────────────────────────────────────────────

  const handleVoid = async (bill: Bill) => {
    try {
      await billsApi.void(bill._id);
      loadBills();
      loadAging();
    } catch (err) {
      console.error("Failed to void bill:", err);
    }
  };

  // ─── Delete Bill ────────────────────────────────────────────────────────────

  const handleDelete = async (bill: Bill) => {
    try {
      await billsApi.remove(bill._id);
      loadBills();
      loadAging();
    } catch (err) {
      console.error("Failed to delete bill:", err);
    }
  };

  // ─── Pay Modal ──────────────────────────────────────────────────────────────

  const openPayModal = (bill: Bill) => {
    setPayingBill(bill);
    const balance = bill.balanceDueCents ?? bill.totalCents - (bill.paidCents || 0);
    setPayAmountDollars(centsToDollars(balance));
    setPayMethod(null);
    setPayBankAccountId(null);
    setShowPayModal(true);
  };

  const closePayModal = () => {
    setShowPayModal(false);
    setPayingBill(null);
    setPayAmountDollars("");
    setPayMethod(null);
    setPayBankAccountId(null);
  };

  const handlePay = async () => {
    if (!payingBill || !payMethod) return;

    const amountCents = dollarsToCents(payAmountDollars);
    if (amountCents <= 0) return;

    setPaying(true);
    try {
      await billsApi.pay(payingBill._id, {
        amountCents,
        paymentMethod: payMethod,
        bankAccountId: payBankAccountId,
      });
      closePayModal();
      loadBills();
      loadAging();
    } catch (err) {
      console.error("Failed to pay bill:", err);
    } finally {
      setPaying(false);
    }
  };

  // ─── SelectOption adapters ──────────────────────────────────────────────────

  const expenseAccountOptions = expenseAccounts.map((a) => ({
    value: a._id,
    label: `${a.code} - ${a.name}`,
  }));

  const bankAccountOptions = bankAccounts.map((a) => ({
    value: a._id,
    label: a.name + (a.accountNumber ? ` (${a.accountNumber})` : ""),
  }));

  const paymentMethodOptions = PAYMENT_METHODS;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Aging Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {/* Total Outstanding */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-slate-500" />
            <p className="text-xs text-slate-500 font-medium">
              Total Payable
            </p>
          </div>
          <p className="text-lg font-bold text-slate-800">
            {formatCents(totalOutstanding)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {totalOutstandingCount} bill{totalOutstandingCount !== 1 ? "s" : ""}
          </p>
        </motion.div>

        {/* Aging buckets */}
        {(Object.keys(agingLabels) as AgingBucket[]).map((bucket, i) => {
          const colors = agingColors[bucket];
          return (
            <motion.div
              key={bucket}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (i + 1) * 0.05 }}
              className={`rounded-xl shadow-sm border p-4 ${colors.bg} ${colors.border}`}
            >
              <p className={`text-xs font-medium ${colors.text} mb-1`}>
                {agingLabels[bucket]}
              </p>
              <p className={`text-lg font-bold ${colors.text}`}>
                {formatCents(computedAging[bucket].totalCents)}
              </p>
              <p className={`text-xs mt-0.5 ${colors.text} opacity-70`}>
                {computedAging[bucket].count} bill
                {computedAging[bucket].count !== 1 ? "s" : ""}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by vendor name or bill number..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
          <button
            onClick={openCreateModal}
            onMouseEnter={() => setNewBillHover(true)}
            onMouseLeave={() => setNewBillHover(false)}
            style={{
              backgroundColor: newBillHover
                ? "var(--color-primary-hover)"
                : "var(--color-primary)",
            }}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            New Bill
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-lg font-medium text-slate-600">
            {search ? "No matching bills" : "No bills found"}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            {search
              ? "Try a different search term"
              : "Create your first bill to get started"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand text-white text-left">
                  <th className="px-4 py-3 font-semibold">Vendor</th>
                  <th className="px-4 py-3 font-semibold">Bill #</th>
                  <th className="px-4 py-3 font-semibold">Bill Date</th>
                  <th className="px-4 py-3 font-semibold">Due Date</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-right">
                    Balance Due
                  </th>
                  <th className="px-4 py-3 font-semibold text-center">
                    Status
                  </th>
                  <th className="px-4 py-3 font-semibold text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((bill, i) => {
                  const balance =
                    bill.balanceDueCents ??
                    bill.totalCents - (bill.paidCents || 0);
                  const overdue = daysOverdue(bill.dueDate);
                  const isOverdue =
                    overdue > 0 &&
                    bill.status !== "paid" &&
                    bill.status !== "void";

                  return (
                    <motion.tr
                      key={bill._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.015 }}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => openEditModal(bill)}
                    >
                      <td className="px-4 py-3 text-slate-800 font-medium max-w-[180px] truncate">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {bill.vendorName || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                        {bill.billNumber ||
                          bill._id.slice(-6).toUpperCase()}
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">
                        {formatDate(bill.billDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        <span
                          className={
                            isOverdue
                              ? "text-red-600 font-medium"
                              : "text-slate-600"
                          }
                        >
                          {formatDate(bill.dueDate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {formatCents(bill.totalCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">
                        {formatCents(balance)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            statusBadgeClasses[bill.status] || "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {statusLabels[bill.status] || bill.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div
                          className="flex items-center justify-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {bill.status === "draft" && (
                            <>
                              <button
                                onClick={() => handleApprove(bill)}
                                className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                                title="Approve"
                              >
                                <ShieldCheck className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(bill)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          {(bill.status === "approved" ||
                            bill.status === "partial") && (
                            <>
                              <button
                                onClick={() => openPayModal(bill)}
                                className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition-colors"
                                title="Pay"
                              >
                                <CreditCard className="h-3.5 w-3.5" />
                              </button>
                              {(bill.paidCents || 0) === 0 && (
                                <button
                                  onClick={() => handleVoid(bill)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                                  title="Void"
                                >
                                  <Ban className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold border-t border-slate-200">
                  <td colSpan={4} className="px-4 py-3 text-xs text-slate-600">
                    Total ({sorted.length} bill
                    {sorted.length !== 1 ? "s" : ""})
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">
                    {formatCents(
                      sorted.reduce((s, b) => s + b.totalCents, 0)
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">
                    {formatCents(
                      sorted.reduce(
                        (s, b) =>
                          s +
                          (b.balanceDueCents ??
                            b.totalCents - (b.paidCents || 0)),
                        0
                      )
                    )}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ─── Create/Edit Bill Modal ──────────────────────────────────────────── */}
      <PremiumModal
        open={showCreateModal}
        onClose={closeCreateModal}
        title={editingBill ? "Edit Bill" : "New Bill"}
        icon={FileText}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={closeCreateModal}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave(true)}
              onMouseEnter={() => setSaveApproveHover(true)}
              onMouseLeave={() => setSaveApproveHover(false)}
              disabled={saving || !formVendorName.trim() || !formDueDate}
              style={{
                backgroundColor: saveApproveHover
                  ? "var(--color-primary-hover)"
                  : "var(--color-primary)",
                opacity:
                  saving || !formVendorName.trim() || !formDueDate ? 0.5 : 1,
              }}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors"
            >
              {saving ? "Saving..." : "Save & Approve"}
            </button>
            <button
              onClick={() => handleSave(false)}
              onMouseEnter={() => setSaveDraftHover(true)}
              onMouseLeave={() => setSaveDraftHover(false)}
              disabled={saving || !formVendorName.trim() || !formDueDate}
              className="px-4 py-2.5 rounded-xl border-2 border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Draft"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Vendor Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vendor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formVendorName}
              onChange={(e) => setFormVendorName(e.target.value)}
              placeholder="Enter vendor name"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Bill Number + Dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Bill Number
              </label>
              <input
                type="text"
                value={formBillNumber}
                onChange={(e) => setFormBillNumber(e.target.value)}
                placeholder="Optional"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Bill Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formBillDate}
                onChange={(e) => setFormBillDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formDueDate}
                onChange={(e) => setFormDueDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes..."
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
            />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                Line Items
              </label>
              <button
                onClick={addLineItem}
                className="flex items-center gap-1 text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Line
              </button>
            </div>

            <div className="space-y-3">
              {formLineItems.map((line, idx) => (
                <div
                  key={idx}
                  className="border border-slate-200 rounded-xl p-3 bg-slate-50/50"
                >
                  <div className="grid grid-cols-12 gap-2">
                    {/* Description */}
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) =>
                          updateLineItem(idx, "description", e.target.value)
                        }
                        placeholder="Description"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    </div>

                    {/* Expense Account */}
                    <div className="col-span-3">
                      <SearchableSelect
                        options={expenseAccountOptions}
                        value={line.accountId || null}
                        onChange={(val) =>
                          updateLineItem(idx, "accountId", val || "")
                        }
                        placeholder="Expense Account"
                      />
                    </div>

                    {/* Amount */}
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.amountDollars}
                        onChange={(e) =>
                          updateLineItem(idx, "amountDollars", e.target.value)
                        }
                        placeholder="Amount"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    </div>

                    {/* Taxable */}
                    <div className="col-span-1 flex items-center justify-center">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={line.taxable}
                          onChange={(e) =>
                            updateLineItem(idx, "taxable", e.target.checked)
                          }
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-[10px] text-slate-500">Tax</span>
                      </label>
                    </div>

                    {/* Tax Amount */}
                    <div className="col-span-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={line.taxAmountDollars}
                        onChange={(e) =>
                          updateLineItem(
                            idx,
                            "taxAmountDollars",
                            e.target.value
                          )
                        }
                        placeholder="Tax"
                        disabled={!line.taxable}
                        className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-emerald-600 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>

                    {/* Remove */}
                    <div className="col-span-1 flex items-center justify-center">
                      <button
                        onClick={() => removeLineItem(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        disabled={formLineItems.length <= 1}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="mt-3 border-t border-slate-200 pt-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-mono text-slate-700">
                  {formatCents(formSubtotalCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">{getTaxLabel(taxConfig)}</span>
                <span className="font-mono text-slate-700">
                  {formatCents(formTaxCents)}
                </span>
              </div>
              <div className="flex justify-between text-sm font-bold">
                <span className="text-slate-800">Total</span>
                <span className="font-mono text-slate-800">
                  {formatCents(formTotalCents)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PremiumModal>

      {/* ─── Pay Bill Modal ──────────────────────────────────────────────────── */}
      <PremiumModal
        open={showPayModal}
        onClose={closePayModal}
        title="Pay Bill"
        icon={CreditCard}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={closePayModal}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePay}
              onMouseEnter={() => setConfirmPayHover(true)}
              onMouseLeave={() => setConfirmPayHover(false)}
              disabled={
                paying ||
                !payMethod ||
                dollarsToCents(payAmountDollars) <= 0
              }
              style={{
                backgroundColor: confirmPayHover
                  ? "var(--color-primary-hover)"
                  : "var(--color-primary)",
                opacity:
                  paying ||
                  !payMethod ||
                  dollarsToCents(payAmountDollars) <= 0
                    ? 0.5
                    : 1,
              }}
              className="px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors"
            >
              {paying ? "Processing..." : "Confirm Payment"}
            </button>
          </div>
        }
      >
        {payingBill && (
          <div className="space-y-4">
            {/* Bill Summary */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Vendor</span>
                <span className="font-medium text-slate-800">
                  {payingBill.vendorName}
                </span>
              </div>
              {payingBill.billNumber && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Bill #</span>
                  <span className="text-slate-700">
                    {payingBill.billNumber}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total</span>
                <span className="font-mono text-slate-700">
                  {formatCents(payingBill.totalCents)}
                </span>
              </div>
              {(payingBill.paidCents || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Already Paid</span>
                  <span className="font-mono text-emerald-700">
                    {formatCents(payingBill.paidCents || 0)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2">
                <span className="text-slate-800">Balance Due</span>
                <span className="font-mono text-slate-800">
                  {formatCents(
                    payingBill.balanceDueCents ??
                      payingBill.totalCents - (payingBill.paidCents || 0)
                  )}
                </span>
              </div>
            </div>

            {/* Amount to Pay */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Amount to Pay
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payAmountDollars}
                  onChange={(e) => setPayAmountDollars(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 pl-8 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={paymentMethodOptions}
                value={payMethod}
                onChange={setPayMethod}
                placeholder="Select payment method..."
              />
            </div>

            {/* Bank Account */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Bank Account
              </label>
              <SearchableSelect
                options={bankAccountOptions}
                value={payBankAccountId}
                onChange={setPayBankAccountId}
                placeholder="Select bank account..."
              />
            </div>
          </div>
        )}
      </PremiumModal>
    </div>
  );
};

export default AccountsPayableTab;
